import { StoredQuery } from 'src/constants/storedQuery';
import {
  STORED_QUERY_COMPETENCY_QUESTION_IRI,
  SYSTEM_QUERY_CREATOR_IRI,
  SYSTEM_QUERY_DATABASE_IRI,
  SYSTEM_QUERY_DESCRIPTION_IRI,
  SYSTEM_QUERY_NAME_IRI,
  SYSTEM_QUERY_SCHEMA_IRI,
  SYSTEM_QUERY_STRING_IRI,
  SYSTEM_REASONING_QUERY_IRI,
  SYSTEM_SHARED_QUERY_IRI,
  SYSTEM_VOICEBOX_QUESTION_IRI,
} from 'src/templates/queries/iris';
import { JsonLdResult } from 'src/templates/queries/types';

const getSingle = <K extends string | number | boolean | undefined>(
  result: JsonLdResult[keyof JsonLdResult],
  key: string
): K | undefined => {
  if (Array.isArray(result)) {
    return getSingle(result[0], key);
  }
  switch (typeof result) {
    case 'object':
      return getSingle(result[key], key);
    default:
      return (result || undefined) as K;
  }
};

export const parseStoredQueriesResults = (results: JsonLdResult[]) => {
  return Object.fromEntries(
    results
      .map((result): [string, StoredQuery] | null => {
        const name = getSingle<string>(result[SYSTEM_QUERY_NAME_IRI], '@value');
        if (!name) {
          return null;
        }

        const types = result['@type'] as string[] | undefined;
        const reasoning = types?.includes(SYSTEM_REASONING_QUERY_IRI) || false;
        const shared = types?.includes(SYSTEM_SHARED_QUERY_IRI) || false;

        const creator = getSingle<string>(
          result[SYSTEM_QUERY_CREATOR_IRI],
          '@value'
        );
        const database = getSingle<string>(
          result[SYSTEM_QUERY_DATABASE_IRI],
          '@value'
        );
        const model = getSingle<string>(
          result[SYSTEM_QUERY_SCHEMA_IRI],
          '@value'
        );
        const query =
          getSingle<string>(result[SYSTEM_QUERY_STRING_IRI], '@value') || '';

        const description = getSingle<string>(
          result[SYSTEM_QUERY_DESCRIPTION_IRI],
          '@value'
        );

        const competencyQuestionId = getSingle<string>(
          result[STORED_QUERY_COMPETENCY_QUESTION_IRI],
          '@value'
        );

        const voiceboxQuestion = getSingle<string>(
          result[SYSTEM_VOICEBOX_QUESTION_IRI],
          '@value'
        );

        return [
          name,
          {
            creator,
            database,
            description,
            model,
            name,
            query,
            reasoning,
            shared,
            competencyQuestionId,
            voiceboxQuestion,
          },
        ];
      })
      .filter((q): q is [string, StoredQuery] => Boolean(q))
  );
};
